import { CustomerProperties, CustomerContext } from '../../types/customerContext';
import { ServiceStatus, ServiceStatusSet } from '../../types/serviceStatus';
import {
  SupportProfile,
  SupportProfileInput,
  ResolveSupportSkuArgs,
  CustomerType,
  CustomerDesignation,
} from '../../types/supportProfile';
import { PricingProfile, PricingProfileInput } from '../../types/pricingProfile';
import { DataverseCustomerDto } from './types';
import { CustomerContextMappingError } from './errors';

export function parseCustomerProperties(propertiesJson: string | null | undefined): CustomerProperties {
  if (!propertiesJson || propertiesJson.trim() === '') {
    return {};
  }

  try {
    const parsed = JSON.parse(propertiesJson);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function deriveServiceStatus(properties: CustomerProperties): ServiceStatusSet {
  const getStatus = (value: unknown): ServiceStatus => {
    if (typeof value === 'number' && value >= 0 && value <= 5) {
      return value as ServiceStatus;
    }
    return ServiceStatus.None;
  };

  return {
    m365Status: getStatus(properties.svc_m365_status),
    googleStatus: getStatus(properties.svc_google_status),
    domainsStatus: getStatus(properties.svc_domains_status),
    antivirusStatus: getStatus(properties.svc_antivirus_status),
    webHostingStatus: getStatus(properties.svc_web_hosting_status),
    dropboxStatus: getStatus(properties.svc_dropbox_status),
    adobeStatus: getStatus(properties.svc_adobe_status),
    rmmStatus: getStatus(properties.svc_rmm_status),
    m365BackupStatus: getStatus(properties.svc_m365_backup_status),
    emailFilteringStatus: getStatus(properties.svc_email_filtering_status)
  };
}

export function resolveSupportSku(
  profile: SupportProfile,
  args: ResolveSupportSkuArgs
): string {
  const { deliveryMode, afterHours, automationModeOverride } = args;
  
  let parts: string[] = [profile.baseSkuPrefix + 'Support'];

  if (profile.designation === 'Core') {
    parts.push('Core');
  } else if (profile.designation === 'CorePlus') {
    parts.push('CorePlus');
  }
  
  if (deliveryMode === 'Automation') {
    const effectiveMode = automationModeOverride || 'Remote';
    parts.push(effectiveMode);
  } else {
    parts.push(deliveryMode);
  }
  
  if (afterHours) {
    parts.push('AH');
  }
  
  return parts.join('.');
}

export function deriveSupportProfile(input: SupportProfileInput): SupportProfile {
  const { customerType, designation } = input;
  
  const baseSkuPrefix = customerType === 'Residential' ? 'R' : 'B';
  
  return {
    customerType,
    designation,
    baseSkuPrefix
  };
}

export function derivePricingProfile(input: PricingProfileInput): PricingProfile {
  return {
    labourOverrides: input.labourOverrides,
    productOverrides: input.productOverrides
  };
}

function normalizeDomains(domains: string | string[] | undefined): string[] {
  if (!domains) {
    return [];
  }
  
  const domainList = Array.isArray(domains) 
    ? domains 
    : domains.split(/[,;\s]+/).filter(d => d);
  
  return domainList
    .map(d => d.toLowerCase().trim())
    .filter(d => d.length > 0);
}

function normalizeAliases(aliases: string[] | undefined): string[] {
  if (!aliases || !Array.isArray(aliases)) {
    return [];
  }
  
  return aliases
    .map(a => a.trim())
    .filter(a => a.length > 0);
}

export function mapDataverseCustomerToCustomerContext(
  dto: DataverseCustomerDto,
  now: Date
): CustomerContext {
  if (!dto.dataverseId) {
    throw new CustomerContextMappingError(
      'MISSING_DATAVERSE_ID',
      'Cannot map customer context: dataverseId is required',
      { dto }
    );
  }
  
  if (dto.repairShoprCustomerId === undefined || dto.repairShoprCustomerId === null) {
    throw new CustomerContextMappingError(
      'MISSING_REPAIRSHOPR_ID',
      'Cannot map customer context: repairShoprCustomerId is required',
      { dto }
    );
  }
  
  const properties = parseCustomerProperties(dto.properties);
  const serviceStatus = deriveServiceStatus(properties);
  
  const customerType: CustomerType = dto.customerType ?? 'Business';
  
  const designationMap: Record<string, CustomerDesignation> = {
    'Core': 'Core',
    'Core+': 'CorePlus',
    'CorePlus': 'CorePlus',
  };
  const resolvedDesignation: CustomerDesignation = dto.designation
    ? (designationMap[dto.designation] ?? undefined)
    : undefined;

  const supportProfile = deriveSupportProfile({
    customerType,
    designation: resolvedDesignation,
  });
  
  const pricingProfile = derivePricingProfile({});
  
  const displayName = dto.customerName ?? 'Unknown Customer';
  const companyNameIfDifferent = dto.companyNameDifferent;
  
  const domains = normalizeDomains(dto.domains);
  const aliases = normalizeAliases(dto.aliases);
  
  return {
    dataverseId: dto.dataverseId,
    repairShoprCustomerId: dto.repairShoprCustomerId,
    displayName,
    companyNameIfDifferent,
    designation: dto.designation,
    phone: dto.phone,
    mobile: dto.mobile,
    domains,
    aliases,
    serviceStatus,
    supportProfile,
    pricingProfile,
    properties,
    rawCustomerContract: dto
  };
}
